import React from "react";
import { format } from "date-fns";
import { HandPlatter, Tag, User, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { useRequest } from "@/components/pages/requests/hooks/useRequests";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";
import { RequestStatus } from "@/types/request";
import { getStatusLabel } from "@/constants/requestConstants";
import {
  getRequestWorkflowStatus,
  getTaskAssignment,
} from "@/components/pages/requests/utils/taskAssignment";

interface RequestPreviewProps {
  requestId: string;
}

const getStatusPillClass = (status: RequestStatus): string => {
  switch (status) {
    case RequestStatus.IN_PROGRESS:
      return "border-skyline-4 text-skyline-2";
    case RequestStatus.APPROVED:
    case RequestStatus.DONE:
    case RequestStatus.COMPLETED:
      return "border-emerald-200 text-emerald-600";
    case RequestStatus.CANCELED:
      return "border-rose-200 text-rose-500";
    case RequestStatus.NEW_TASK:
      return "border-amber-200 text-amber-600";
    default:
      return "border-neutral-200 text-neutral-600";
  }
};

export const RequestBookingInfoDisplay: React.FC<RequestPreviewProps> = ({
  requestId,
}) => {
  const { capabilities } = useAccessCapabilities();
  const { data: request, isLoading } = useRequest(
    requestId,
    capabilities.canUseRequestsSystem,
  );

  if (!capabilities.canUseRequestsSystem) return null;
  if (isLoading || !request) return null;

  const status = getRequestWorkflowStatus(request);
  const { assignedTeamName, assignedStaffName } = getTaskAssignment(
    request.tasks?.[0],
    request.service,
  );
  const assigneeName = assignedTeamName || assignedStaffName;
  const isTeam = Boolean(assignedTeamName);

  const guestName = request.guest?.profile
    ? `${request.guest.profile.firstName ?? ""} ${request.guest.profile.lastName ?? ""}`.trim()
    : "";

  const timeSource = request.createdAt ?? request.dueDateTime;
  const timeLabel = timeSource ? format(new Date(timeSource), "h:mm a") : "";

  const quantity = request.quantity ?? 1;
  const serviceName = request.service?.name ?? "";
  const price = request.service?.price;

  return (
    <div className="flex w-[100%] flex-col gap-3 rounded-xl border border-neutral-200 bg-white p-3">
      {/* Header: room + guest + time */}
      <div className="flex flex-row items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {request.room?.number != null && (
            <span className="bg-monochrome-core-2 flex h-4 min-w-[23px] shrink-0 items-center justify-center rounded-[8px] px-1 text-xs font-semibold text-white">
              {request.room.number}
            </span>
          )}
          <span className="text-eathy-comfort-1 truncate text-xs font-medium">
            {guestName}
          </span>
        </div>
        {timeLabel && (
          <span className="shrink-0 text-xs font-light text-neutral-400">
            {timeLabel}
          </span>
        )}
      </div>

      {/* Body: service + price/guests */}
      <div className="flex flex-row items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="bg-royal-glow-5 text-royal-glow-3 border-royal-glow-4 flex size-8 shrink-0 items-center justify-center rounded-[6px] border">
            <HandPlatter className="size-4" />
          </span>
          <span className="text-eathy-comfort-1 min-w-0 text-sm font-medium break-words">
            {serviceName}
            {quantity > 1 ? ` ×${quantity}` : ""}
          </span>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          {quantity > 1 && (
            <span className="flex items-center gap-1 text-xs text-neutral-500">
              <Users className="size-3.5" />
              {quantity} guests
            </span>
          )}
          {price != null && price !== "" && (
            <span className="text-skyline-2 flex items-center gap-1 text-xs">
              <Tag className="size-3" />
              {price}
            </span>
          )}
        </div>
      </div>

      {/* Footer: status + assignee */}
      <div className="flex flex-row items-center gap-2">
        <span
          className={cn(
            "flex h-6 flex-1 items-center justify-center rounded-[8px] border text-sm font-medium",
            getStatusPillClass(status),
          )}
        >
          {getStatusLabel(status)}
        </span>
        <span className="text-eathy-comfort-1 flex h-6 flex-1 items-center justify-center gap-1.5 rounded-[8px] border border-neutral-200 text-sm">
          {isTeam ? (
            <Users className="size-4 shrink-0 text-neutral-500" />
          ) : (
            <User className="size-4 shrink-0 text-neutral-500" />
          )}
          <span className="truncate">{assigneeName || "—"}</span>
        </span>
      </div>
    </div>
  );
};
